import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Check, Calendar, Users, Euro, Clock, MapPin, 
  Phone, Mail, Home, ArrowRight, Loader2, Download,
  Star, Shield, Coffee
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Real estate photos from the site
const ESTATE_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/1fxxzk5s_IMG_4445.jpg", // Vue aérienne du domaine
  "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/6edms9f4_jardin.jpg", // Nature/jardin
  "https://customer-assets.emergentagent.com/job_house-booking-1/artifacts/qn91zz8c_piscinenormont.png" // Piscine
];

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sessionId) {
      fetchBookingDetails();
    } else {
      setError("Session invalide");
      setLoading(false);
    }
  }, [sessionId]);

  const fetchBookingDetails = async () => {
    try {
      // First verify payment status
      await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      
      // Then get booking details
      const response = await axios.get(`${API_URL}/api/payments/booking-details/${sessionId}`);
      setBooking(response.data);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError("Impossible de charger les détails de la réservation");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#C5A059] mx-auto mb-4" />
          <p className="text-[#666666] font-['Outfit']">Chargement de votre réservation...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">!</span>
          </div>
          <h2 className="text-2xl font-['Playfair_Display'] mb-2">Une erreur est survenue</h2>
          <p className="text-[#666666] font-['Outfit'] mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/booking')}
            className="bg-[#C5A059] hover:bg-[#A6864A] text-white"
          >
            Retour aux réservations
          </Button>
        </div>
      </div>
    );
  }

  const checkIn = new Date(booking.check_in);
  const checkOut = new Date(booking.check_out);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="booking-success-page">
      {/* Hero Section with Success Message */}
      <div className="relative bg-[#0A0A0A] text-white pt-24 pb-16">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={ESTATE_PHOTOS[0]} 
            alt="Domaine du Normont" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 to-[#0A0A0A]" />
        </div>
        
        <div className="relative container-custom text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl animate-bounce-slow">
            <Check className="w-14 h-14 text-white" strokeWidth={3} />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-['Playfair_Display'] mb-4">
            Réservation confirmée !
          </h1>
          <p className="text-xl text-white/80 font-['Outfit'] mb-2">
            Merci pour votre confiance
          </p>
          <p className="text-[#C5A059] font-['Outfit']">
            Un email de confirmation a été envoyé à {booking.user_email}
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Booking Reference Card */}
          <div className="bg-white border border-[#E5E5E5] shadow-lg -mt-8 relative z-10 mb-8">
            <div className="bg-[#C5A059] text-white px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-['Outfit'] opacity-80">Numéro de réservation</p>
                  <p className="text-2xl font-['Playfair_Display']">#{booking.id?.slice(-8).toUpperCase()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-['Outfit'] opacity-80">Statut</p>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    <span className="font-['Outfit'] font-semibold">Confirmée</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 md:p-8">
              {/* Dates & Guests Grid */}
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-[#FAFAFA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#666666] font-['Outfit']">Arrivée</p>
                    <p className="text-lg font-['Playfair_Display']">
                      {format(checkIn, 'd MMMM yyyy', { locale: fr })}
                    </p>
                    <p className="text-sm text-[#C5A059] font-['Outfit']">à partir de 16h00</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-[#FAFAFA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#666666] font-['Outfit']">Départ</p>
                    <p className="text-lg font-['Playfair_Display']">
                      {format(checkOut, 'd MMMM yyyy', { locale: fr })}
                    </p>
                    <p className="text-sm text-[#C5A059] font-['Outfit']">avant 11h00</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-[#FAFAFA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#666666] font-['Outfit']">Durée</p>
                    <p className="text-lg font-['Playfair_Display']">
                      {booking.nights} nuit{booking.nights > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-[#FAFAFA] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#666666] font-['Outfit']">Voyageurs</p>
                    <p className="text-lg font-['Playfair_Display']">
                      {booking.guests} personne{booking.guests > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-[#E5E5E5] my-6"></div>

              {/* Price Summary */}
              <div className="bg-[#FAFAFA] p-6 rounded-lg">
                <h3 className="font-['Playfair_Display'] text-lg mb-4">Récapitulatif du paiement</h3>
                <div className="space-y-3">
                  <div className="flex justify-between font-['Outfit']">
                    <span className="text-[#666666]">{booking.nights} nuit{booking.nights > 1 ? 's' : ''} x {booking.price_per_night}€</span>
                    <span>{booking.nights * booking.price_per_night}€</span>
                  </div>
                  {booking.cleaning_fee > 0 && (
                    <div className="flex justify-between font-['Outfit']">
                      <span className="text-[#666666]">Option ménage</span>
                      <span>{booking.cleaning_fee}€</span>
                    </div>
                  )}
                  {booking.discount > 0 && (
                    <div className="flex justify-between font-['Outfit'] text-green-600">
                      <span>Réduction</span>
                      <span>-{booking.discount}€</span>
                    </div>
                  )}
                  <div className="border-t border-[#E5E5E5] pt-3 flex justify-between font-['Outfit']">
                    <span className="font-semibold text-lg">Total payé</span>
                    <span className="font-semibold text-lg text-[#C5A059]">{booking.total_price}€</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Property Info Card */}
          <div className="bg-white border border-[#E5E5E5] mb-8">
            <div className="p-6 border-b border-[#E5E5E5]">
              <h3 className="font-['Playfair_Display'] text-xl">Votre destination</h3>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-start gap-3 mb-4">
                    <MapPin className="w-5 h-5 text-[#C5A059] mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-['Playfair_Display'] text-lg">Domaine du Normont</p>
                      <p className="text-[#666666] font-['Outfit']">
                        À 45 minutes de Paris<br />
                        Propriété de 7 hectares
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 mb-4">
                    <Phone className="w-5 h-5 text-[#C5A059] mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-['Outfit'] font-medium">Contact</p>
                      <p className="text-[#666666] font-['Outfit']">contact@domainedunormont.fr</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="text-center p-3 bg-[#FAFAFA] rounded">
                      <Home className="w-5 h-5 text-[#C5A059] mx-auto mb-1" />
                      <p className="text-xs text-[#666666] font-['Outfit']">Maison entière</p>
                    </div>
                    <div className="text-center p-3 bg-[#FAFAFA] rounded">
                      <Coffee className="w-5 h-5 text-[#C5A059] mx-auto mb-1" />
                      <p className="text-xs text-[#666666] font-['Outfit']">Équipée</p>
                    </div>
                    <div className="text-center p-3 bg-[#FAFAFA] rounded">
                      <Shield className="w-5 h-5 text-[#C5A059] mx-auto mb-1" />
                      <p className="text-xs text-[#666666] font-['Outfit']">Sécurisé</p>
                    </div>
                  </div>
                </div>
                
                {/* Photo Gallery */}
                <div className="grid grid-cols-2 gap-2">
                  <img 
                    src={ESTATE_PHOTOS[0]} 
                    alt="Domaine" 
                    className="col-span-2 w-full h-32 object-cover rounded-lg"
                  />
                  <img 
                    src={ESTATE_PHOTOS[1]} 
                    alt="Intérieur" 
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <img 
                    src={ESTATE_PHOTOS[2]} 
                    alt="Extérieur" 
                    className="w-full h-24 object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* What's Next Card */}
          <div className="bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] text-white p-8 rounded-lg">
            <h3 className="font-['Playfair_Display'] text-2xl mb-6">Prochaines étapes</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#C5A059] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-['Outfit'] font-semibold mb-1">Email de confirmation</p>
                  <p className="text-sm text-white/70 font-['Outfit']">
                    Vérifiez votre boîte mail pour les détails complets
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#C5A059] rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-['Outfit'] font-semibold mb-1">J-7 avant l'arrivée</p>
                  <p className="text-sm text-white/70 font-['Outfit']">
                    Vous recevrez les instructions d'accès
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#C5A059] rounded-full flex items-center justify-center flex-shrink-0">
                  <Star className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-['Outfit'] font-semibold mb-1">Après votre séjour</p>
                  <p className="text-sm text-white/70 font-['Outfit']">
                    Partagez votre expérience avec un avis
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
            <Button 
              onClick={() => navigate('/client')}
              className="bg-[#C5A059] hover:bg-[#A6864A] text-white px-8 py-6 text-lg"
              data-testid="go-to-dashboard-btn"
            >
              Voir mes réservations
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-8 py-6 text-lg"
            >
              Retour à l'accueil
            </Button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default BookingSuccessPage;
